<template lang="html">
  <section class="onboarding">
    <DynamoLogo appear/>
    <transition :name="transitionName" appear :mode="transitionMode">
      <div v-if="step === 0 || !$route.query.step" class="wrapper" key="page1">
        <h1 class="no-svg">Welcome to your new journal!</h1>
        <Button @click.native="nextPage">Get Started</Button>
        <a href="#" @click.prevent="$refs.dataInput.click">Import existing data</a>
        <div class="legal">
          <router-link :to="{ query: { showPrivacyPolicy: true } }">Privacy Policy</router-link>
          <a href="https://amxmln.com/legal/" target="_blank" rel="noreferrer noopener nofollow">Impressum</a>
        </div>
      </div>
      <div v-if="step === 1" class="wrapper" key="page2">
        <svg viewBox="0 0 68 69" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path class="pen" d="M23.3333 14.2222L14.0809 4.96977C13.2998 4.18872 12.0335 4.18872 11.2524 4.96977L6.96976 9.25245C6.18871 10.0335 6.18871 11.2998 6.96976 12.0809L16.2222 21.3333M23.3333 14.2222L58.8889 49.7778L62.4444 60.4444L51.7778 56.8889L16.2222 21.3333M23.3333 14.2222L16.2222 21.3333" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          <path class="line" d="M66 67H2" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          <path class="burst" d="M6 19H22" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          <path class="burst" d="M3.07178 11L16.9282 3" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          <path class="burst" d="M3.07178 27L16.9282 35" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <h1>Jot down your thoughts as bullets or continuous text</h1>
        <Button class="large-mb" @click.native="nextPage">Next</Button>
      </div>
      <div v-if="step === 2" class="wrapper" key="page3">
        <svg viewBox="0 0 68 68" fill="none" xmlns="http://www.w3.org/2000/svg">
          <ellipse class="cheek" cx="15.3" cy="37.4" rx="5.1" ry="3.4" fill="white"/>
          <ellipse class="cheek" cx="52.7" cy="37.4" rx="5.1" ry="3.4" fill="white"/>
          <circle cx="34" cy="34" r="30.6" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          <path class="eye" d="M13.6 27.2L18.9858 21.8142C19.7668 21.0332 21.0331 21.0332 21.8142 21.8142L27.2 27.2" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          <path class="eye" d="M40.8 27.2L46.1858 21.8142C46.9668 21.0332 48.2332 21.0332 49.0142 21.8142L54.4 27.2" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          <path class="mouth" d="M42.5 48.7224C39.9157 50.2145 36.9841 51 34 51C31.0159 51 28.0843 50.2145 25.5 48.7224" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <h1>Keep track of your emotions.</h1>
        <Button class="large-mb" @click.native="nextPage">Next</Button>
      </div>
      <div v-if="step === 3" class="wrapper" key="page4">
        <svg class="shield" viewBox="0 0 58 72" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M29 70C29 70 56 63.3993 56 42.7056V13.5944C56 11.9376 54.6562 10.6008 52.9999 10.5569C43.1711 10.2961 34.7737 8.56628 29 2C23.2263 8.56628 14.8289 10.2961 5.00006 10.5569C3.34379 10.6008 2 11.9376 2 13.5944V42.7056C2 63.3993 29 70 29 70Z" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          <path class="check" d="M17 36L25 44L41 28" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <h1>Securely on your device and only on your device.</h1>
        <Button class="large-mb" @click.native="nextPage">Next</Button>
      </div>
      <div v-if="step === 4" class="wrapper" key="page5">
        <svg viewBox="0 0 100 68" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect class="full-note" x="26" y="2" width="48" height="48" rx="6" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          <rect class="side-note left" x="26" y="2" width="48" height="48" rx="6" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          <rect class="cursor" x="16" y="60" rx="12"/>
        </svg>
        <h1>Swipe left and right to travel through time.</h1>
        <Button class="large-mb" @click.native="nextPage">Great</Button>
      </div>
      <div v-if="step === 5" class="wrapper" key="page6">
        <svg viewBox="0 0 74 74" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect class="rainbow-note" x="2" y="2" width="18" height="18" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          <rect class="rainbow-note expander" x="28" y="2" width="18" height="18" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          <rect class="rainbow-note" x="54" y="2" width="18" height="18" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          <rect class="rainbow-note" x="2" y="28" width="18" height="18" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          <rect class="rainbow-note" x="28" y="28" width="18" height="18" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          <rect class="rainbow-note" x="54" y="28" width="18" height="18" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          <rect class="rainbow-note" x="2" y="54" width="18" height="18" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          <rect class="rainbow-note" x="28" y="54" width="18" height="18" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          <rect class="rainbow-note" x="54" y="54" width="18" height="18" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          <rect class="cursor multinote" rx="12"/>
        </svg>
        <h1>Swipe up to access the rainbow of your emotions.</h1>
        <Button class="large-mb" @click.native="nextPage">Got it</Button>
      </div>
      <div v-if="step === 6" class="wrapper" key="page7">
        <h1 class="no-svg">What’s your first name?</h1>
        <input v-model="username" placeholder="Anon" type="text">
        <Button @click.native="completeSetup">Start Journalling</Button>
        <a href="#" @click.prevent="$refs.dataInput.click">Import existing data</a>
      </div>
    </transition>
    <transition name="fade">
      <ProgressBubbles v-if="showBubbles" :current="step" :total="5" @click="handleBubbleNavigation" />
    </transition>
    <input accept=".json" ref="dataInput" style="display: none" type="file" @change="readData">
    <Modal class="loading-modal" :dismissable="false" :show="showLoading">
      <Loader :percentage="loadingPercentage" />
    </Modal>
  </section>
</template>

<script>
import Button from '@/components/Button.vue';
import DynamoLogo from '@/components/DynamoLogo.vue';
import Loader from '@/components/Loader.vue';
import Modal from '@/components/Modal.vue';
import ProgressBubbles from '@/components/ProgressBubbles.vue';

export default {
  beforeDestroy() {
    window.removeEventListener('resize', this.setTransitionMode);
  },
  components: {
    Button,
    DynamoLogo,
    Loader,
    Modal,
    ProgressBubbles,
  },
  computed: {
    showBubbles() {
      if (this.step > 0 && this.step < 6) return true;
      return false;
    },
    step() {
      return Number.parseInt(this.$route.query.step, 10);
    },
  },
  data() {
    return {
      back: false,
      loadingPercentage: 0,
      showLoading: false,
      transitionMode: undefined,
      transitionName: 'slide',
      username: '',
    };
  },
  methods: {
    completeSetup() {
      if (this.username.length > 0) this.$store.dispatch('setName', this.username);
      this.$store.dispatch('setOnboardingComplete', true);
      this.$router.push('/');
    },
    handleBubbleNavigation(step) {
      if (step !== this.$route.query.step) this.$router.push({ query: { step } });
    },
    nextPage() {
      const nextStep = !this.$route.query.step || Number.isNaN(this.$route.query.step) ? 1 : Number.parseInt(this.$route.query.step, 10) + 1;
      this.$router.push({ query: { step: nextStep } });
    },
    progressCallback({ completedRows, totalRows }) {
      this.loadingPercentage = Math.round((completedRows / totalRows) * 100);
    },
    async readData(e) {
      this.showLoading = true;
      const success = await this.$store.dispatch('importDB', { file: e.target.files[0], progressCallback: this.progressCallback });
      e.target.value = null; // clear the input so it can accept the same file again
      this.showLoading = false;
      this.loadingPercentage = 0;
      if (success) this.$router.push('/');
    },
    setTransitionMode() {
      if (window.innerWidth >= 640) this.transitionMode = 'out-in';
      else this.transitionMode = undefined;
    },
  },
  mounted() {
    this.setTransitionMode();
    window.addEventListener('resize', this.setTransitionMode);
  },
  watch: {
    step: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal < oldVal || Number.isNaN(newVal)) this.transitionName = 'slide-back';
        else this.transitionName = 'slide';
      },
    },
  },
};
</script>

<style lang="stylus" scoped>
.onboarding
  padding: 24px 32px
  height: 100%

  .dynamo-logo
    margin-top: 24px

  .progress-bubbles
    position: fixed
    bottom: 24px
    width: calc(100% - 64px)
    max-width: 576px

    &.fade-enter-active,
    &.fade-leave-active
      transition: opacity 200ms ease

      &.fade-enter,
      &.fade-leave-to
        opacity: 0

  .wrapper
    display: flex
    flex-direction: column
    justify-content: center
    height: calc(100% - 88px)

    &.slide-enter-active,
    &.slide-leave-active,
    &.slide-back-enter-active,
    &.slide-back-leave-active
      transition: transform 300ms ease
      position: fixed
      width: calc(100% - 64px)
      height: calc(100% - 136px)

      @media screen and (min-width: 640px)
        position: unset
        width: unset
        height: calc(100% - 88px)
        transition: transform 150ms ease, opacity 150ms ease

      &.slide-enter,
      &.slide-back-leave-to
        transform: translateX(100vw)

        @media screen and (min-width: 640px)
          transform: translateX(128px)
          opacity: 0

      &.slide-leave-to,
      &.slide-back-enter
        transform: translateX(-100vw)

        @media screen and (min-width: 640px)
          transform: translateX(-128px)
          opacity: 0

    svg
      margin: auto
      height: 13.5%
      overflow: visible

      &.hide-overflow
        overflow: hidden

      .pen
        animation: movePen forwards 500ms ease

        @keyframes movePen
          from
            transform: translateX(-100%)
          to
            transform: translateX(0)

      .line
        stroke-dasharray: 64px
        animation: drawLine forwards 500ms ease

        @keyframes drawLine
          from
            stroke-dashoffset: 64px
          to
            stroke-dashoffset: 128px

      .burst
        transform: translate(105%, calc(100% - 21px))
        stroke-dasharray: 16.1px
        stroke-dashoffset: 16.1px
        animation: burst forwards 300ms ease-out 450ms

        @keyframes burst
          from
            stroke-dashoffset: 48.3px
          to
            stroke-dashoffset: 16.1px

      .eye,
      .mouth,
      .cheek
        animation: cheer 1s ease infinite alternate

        @keyframes cheer
          from
            transform: none
          to
            transform: translateY(-5%)

      .cheek
        animation-delay: 200ms

      &.shield
        animation: bounce 2s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards
        animation-delay: 100ms
        transform: translateY(-24px)

        @keyframes bounce
          0%
          10%
            transform: translateY(-24px)
          20%
          80%
          90%
          100%
            transform: none
            // transform: translateY(-8px)

      .check
        stroke-dasharray: 34px
        animation: check 2s ease-out

        @keyframes check
          0%
          10%
            stroke-dashoffset: 34px
          20%
          80%
          90%
          100%
            stroke-dashoffset: 0
            // stroke-dashoffset: -34px

      .full-note
        transform-origin: right
        animation: moveNoteLeftRight 4s cubic-bezier(0.175, 0.885, 0.32, 1.275) infinite
        animation-delay: 400ms

        @keyframes moveNoteLeftRight
          0%
          10%
            transform: none
            opacity: 1
          25%
          65%
            transform: translateX(48px) scale(0.8)
            opacity: 0
          80%
          100%
            transform: none
            opacity: 1

      .side-note.left
        transform-origin: left
        animation: scaleLeftNote 4s cubic-bezier(0.175, 0.885, 0.32, 1.275) infinite
        animation-delay: 400ms

        @keyframes scaleLeftNote
          0%
          10%
            transform: translateX(-48px) scale(0.8)
            opacity: 0
          25%
          65%
            transform: none
            opacity: 1
          80%
          100%
            transform: translateX(-48px) scale(0.8)
            opacity: 0

      .cursor
        width: 24px
        height: 24px
        animation: cursorSingleNote 4s cubic-bezier(0.175, 0.885, 0.32, 1.275) infinite
        fill: alpha(white, 0.5)

        &.multinote
          x: -32px
          animation: cursorMultiNote 4s cubic-bezier(0.175, 0.885, 0.32, 1.275) infinite

        @keyframes cursorSingleNote
          0%
          10%
            x: -16px
            width: 24px
          20%
            x: -16px
            width: 132px
          25%
          65%
            x: 92px
            width: 24px
          75%
            x: -16px
            width: 132px
          80%
          100%
            x: -16px
            width: 24px

        @keyframes cursorMultiNote
          0%
          10%
            y: 66px
            height: 24px
          20%
            y: -16px
            height: 106px
          25%
          65%
            y: -16px
            height: 24px
          75%
            y: -16px
            height: 106px
          80%
          100%
            y: 66px
            height: 24px

      .expander
        transform-origin: center 2px
        animation: shrink 4s cubic-bezier(0.175, 0.885, 0.32, 1.275) infinite
        animation-delay: 400ms
        rx: 3px
        transform: scale(3.8889)
        stroke-width: 1.3

        @keyframes shrink
          0%
          10%
            rx: 3px
            transform: scale(3.8889)
            stroke-width: 1.3
          25%
          65%
            rx: 0
            transform: none
            stroke-width: 4
          80%
          100%
            rx: 3px
            transform: scale(3.8889)
            stroke-width: 1.3

      .rainbow-note:not(.expander)
        animation: fade 4s ease infinite
        animation-delay: 400ms
        opacity: 0

        @keyframes fade
          0%
          10%
            opacity: 0
          15%
          65%
            opacity: 1
          70%
          100%
            opacity: 0

    h1
      text-align: center
      margin: 0

      &.no-svg
        margin-top: calc(50vh - 112px)
        margin-bottom: 1rem

    .button
      margin-top: auto
      margin-bottom: 44px

      &.large-mb
        margin-bottom: 62px

    input
      flex-shrink: 0

    a
      font-size: 14px
      opacity: 0.38
      text-align: center
      color: white
      text-decoration: none

    .legal
      text-align: center

      a:not(:last-child)
        margin-right: 1rem

  .modal.loading-modal
      >>> h1,
      >>> h2
        margin: 0

      >>> h1
        font-size: 2.5rem

      >>> h2
        margin-top: -10px
</style>
